import {
  PaymentPlanDTO,
  PaymentPlanDTOStatusEnum,
  PaymentPlanInstalmentDTOStatusEnum,
  PaymentPlanInstalmentReschedulePreviewDTO,
  TenantDTO,
} from '@reposit/api-client';
import { sortBy, startCase, toLower } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { penceToPounds } from '../../utils/common.utils';
import { PAYMENT_PLAN_PRODUCT_TYPE_LABELS } from '../../utils/payment-plan.utils';
import Button from '../Button';
import IdentifierValue from '../IdentifierValue';
import InlineTable, { InlineTableItem } from '../InlineTable';
import Modal from '../Modal';
import ModifyPaymentPlanInstalmentModal, { isDeletablePaymentPlanInstalmentStatus } from '../ModifyPaymentPlanInstalmentModal';
import RepositCard from '../Reposit/RepositCard';
import { Table, Tbody, Td, Thead, Tr } from '../Table';
import { Header3 } from '../Typography';

interface PaymentPlanProps {
  paymentPlan: PaymentPlanDTO;
  paymentPlanTenant: TenantDTO;
  manualPaymentsTotal: number;
  onDeleteInstalment?: (instalmentId: string) => Promise<boolean> | boolean;
  deletingInstalmentId?: string;
  onPreviewInstalmentReschedule?: (
    instalmentId: string,
    rescheduledDate: string,
  ) => Promise<PaymentPlanInstalmentReschedulePreviewDTO | undefined>;
  onRescheduleInstalment?: (
    instalmentId: string,
    rescheduledDate: string,
    expectedCurrentScheduleHash: string,
  ) => Promise<boolean> | boolean;
  previewingRescheduleInstalmentId?: string;
  reschedulingInstalmentId?: string;
  onOpenInstalmentDesigner?: () => void;
  onToggleTenantDeferral?: (enabled: boolean) => Promise<boolean> | boolean;
  togglingTenantDeferral?: boolean;
}

interface PaymentPlanModalProps {
  paymentPlan: PaymentPlanDTO;
  onDismiss: () => void;
  onDeleteInstalment?: (instalmentId: string) => Promise<boolean> | boolean;
  deletingInstalmentId?: string;
  onPreviewInstalmentReschedule?: (
    instalmentId: string,
    rescheduledDate: string,
  ) => Promise<PaymentPlanInstalmentReschedulePreviewDTO | undefined>;
  onRescheduleInstalment?: (
    instalmentId: string,
    rescheduledDate: string,
    expectedCurrentScheduleHash: string,
  ) => Promise<boolean> | boolean;
  previewingRescheduleInstalmentId?: string;
  reschedulingInstalmentId?: string;
}

interface PaymentPlanInstalmentTableInstalment {
  id?: string;
  instalmentNumber?: number;
  dueDate: string;
  amount: number;
  status: string;
  source?: string;
}

interface PaymentPlanInstalmentTableProps {
  instalments: PaymentPlanInstalmentTableInstalment[];
  showInstalmentIds?: boolean;
  deletingInstalmentId?: string;
  previewingRescheduleInstalmentId?: string;
  reschedulingInstalmentId?: string;
  onSelectInstalmentAction?: (instalmentId: string) => void;
  instalmentActionLabel?: string;
  instalmentActionMode?: 'delete' | 'modify';
}

const ActionButton = styled(Button)`
  margin-left: 0;
`;

const TenantDeferralValue = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  button {
    margin: 0;
  }
`;

const PAYMENT_PLAN_MODAL_HEIGHT = '500px';

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;

  button {
    margin: 0;
  }
`;

const PaymentPlanScheduleTable = styled(Table)`
  td:nth-child(1) {
    width: 200px;
  }

  td:nth-child(2) {
    text-align: center;
    width: 80px;
  }

  td:nth-child(3) {
    width: 110px;
  }

  td:nth-child(4),
  td:nth-child(5) {
    width: 90px;
  }

  td:nth-child(6) {
    width: 180px;
  }

  td:nth-child(7) {
    width: 90px;
  }
`;

const formatISOStringDate = (date: string) => moment(date).format('DD/MM/YYYY');
const formatDate = (date: string) => moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
const formatSource = (source?: string) => (source ? startCase(toLower(source)) : '—');

export const PaymentPlanInstalmentTable: React.SFC<PaymentPlanInstalmentTableProps> = ({
  instalments,
  showInstalmentIds = false,
  deletingInstalmentId,
  previewingRescheduleInstalmentId,
  reschedulingInstalmentId,
  onSelectInstalmentAction,
  instalmentActionLabel = 'Modify',
  instalmentActionMode = 'modify',
}) => {
  const sortedInstalments = sortBy(instalments, (i) => (i.instalmentNumber === undefined ? i.dueDate : i.instalmentNumber));
  const showActions = !!onSelectInstalmentAction;
  const InstalmentTable = showInstalmentIds ? PaymentPlanScheduleTable : Table;

  return (
    <InstalmentTable>
      <Thead>
        <Tr>
          {showInstalmentIds ? <Td>ID</Td> : null}
          <Td>Number</Td>
          <Td>Due Date</Td>
          <Td>Amount</Td>
          <Td>Status</Td>
          {showInstalmentIds ? <Td>Source</Td> : null}
          {showActions ? <Td>Actions</Td> : null}
        </Tr>
      </Thead>
      <Tbody>
        {sortedInstalments &&
          sortedInstalments.length > 0 &&
          sortedInstalments.map((instalment, i) => {
            const canDeleteInstalment = isDeletablePaymentPlanInstalmentStatus(instalment.status);
            const canModifyInstalment = canDeleteInstalment || instalment.status === PaymentPlanInstalmentDTOStatusEnum.PENDING;
            const isDeleteAction = instalmentActionMode === 'delete';
            const canShowAction = isDeleteAction ? canDeleteInstalment : canModifyInstalment;
            return (
              <Tr key={instalment.id || `${instalment.dueDate}-${i}`}>
                {showInstalmentIds ? <Td>{instalment.id ? <IdentifierValue>{instalment.id}</IdentifierValue> : '—'}</Td> : null}
                <Td>{instalment.instalmentNumber === undefined ? i + 1 : instalment.instalmentNumber}</Td>
                <Td>{formatDate(instalment.dueDate)}</Td>
                <Td>£{penceToPounds(instalment.amount)}</Td>
                <Td>{startCase(instalment.status)}</Td>
                {showInstalmentIds ? <Td>{formatSource(instalment.source)}</Td> : null}
                {showActions ? (
                  <Td>
                    {canShowAction && onSelectInstalmentAction && instalment.id ? (
                      <Button
                        buttonType={isDeleteAction ? 'warning' : undefined}
                        disabled={
                          deletingInstalmentId === instalment.id ||
                          previewingRescheduleInstalmentId === instalment.id ||
                          Boolean(reschedulingInstalmentId)
                        }
                        mini="true"
                        noArrow
                        onClick={() => onSelectInstalmentAction(instalment.id as string)}
                        type="button"
                      >
                        {instalmentActionLabel}
                      </Button>
                    ) : null}
                  </Td>
                ) : null}
              </Tr>
            );
          })}
      </Tbody>
    </InstalmentTable>
  );
};

const PaymentPlanModal: React.SFC<PaymentPlanModalProps> = ({
  paymentPlan,
  onDismiss,
  onDeleteInstalment,
  deletingInstalmentId,
  onPreviewInstalmentReschedule,
  onRescheduleInstalment,
  previewingRescheduleInstalmentId,
  reschedulingInstalmentId,
}) => {
  const { instalments } = paymentPlan;
  const [selectedInstalmentId, setSelectedInstalmentId] = useState<string | undefined>();
  const selectedInstalment = instalments.find((instalment) => instalment.id === selectedInstalmentId);
  const isPaymentPlanActive = paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE;
  const canDeleteInstalments = Boolean(isPaymentPlanActive && onDeleteInstalment);
  const canModifyInstalments = Boolean(isPaymentPlanActive && onPreviewInstalmentReschedule && onRescheduleInstalment);
  const canManageInstalments = canDeleteInstalments || canModifyInstalments;

  return (
    <Modal onDismiss={onDismiss} scrollable fixHeight height={PAYMENT_PLAN_MODAL_HEIGHT} maxWidth="1080px">
      <Header3 style={{ marginBottom: 15 }}>Payment Plan Schedule</Header3>
      <PaymentPlanInstalmentTable
        instalments={instalments}
        showInstalmentIds
        deletingInstalmentId={deletingInstalmentId}
        previewingRescheduleInstalmentId={previewingRescheduleInstalmentId}
        reschedulingInstalmentId={reschedulingInstalmentId}
        onSelectInstalmentAction={canManageInstalments ? setSelectedInstalmentId : undefined}
        instalmentActionLabel={canModifyInstalments ? 'Modify' : 'Delete'}
        instalmentActionMode={canModifyInstalments ? 'modify' : 'delete'}
      />
      {selectedInstalment && canManageInstalments ? (
        <ModifyPaymentPlanInstalmentModal
          key={selectedInstalment.id}
          instalment={selectedInstalment}
          isDeleting={deletingInstalmentId === selectedInstalment.id}
          isPreviewing={previewingRescheduleInstalmentId === selectedInstalment.id}
          isConfirming={reschedulingInstalmentId === selectedInstalment.id}
          onDismiss={() => setSelectedInstalmentId(undefined)}
          onDelete={
            canDeleteInstalments && onDeleteInstalment
              ? async () => {
                  const deleted = await onDeleteInstalment(selectedInstalment.id as string);
                  if (deleted) {
                    setSelectedInstalmentId(undefined);
                    onDismiss();
                  }
                  return deleted;
                }
              : undefined
          }
          onPreviewReschedule={
            canModifyInstalments && onPreviewInstalmentReschedule
              ? (rescheduledDate) => onPreviewInstalmentReschedule(selectedInstalment.id as string, rescheduledDate)
              : undefined
          }
          onConfirmReschedule={
            canModifyInstalments && onRescheduleInstalment
              ? async (rescheduledDate, expectedCurrentScheduleHash) => {
                  const rescheduled = await onRescheduleInstalment(
                    selectedInstalment.id as string,
                    rescheduledDate,
                    expectedCurrentScheduleHash,
                  );
                  if (rescheduled) {
                    setSelectedInstalmentId(undefined);
                    onDismiss();
                  }
                  return rescheduled;
                }
              : undefined
          }
        />
      ) : null}
    </Modal>
  );
};

export const PaymentPlan: React.SFC<PaymentPlanProps> = ({
  paymentPlan,
  paymentPlanTenant,
  manualPaymentsTotal,
  onDeleteInstalment,
  deletingInstalmentId,
  onPreviewInstalmentReschedule,
  onRescheduleInstalment,
  previewingRescheduleInstalmentId,
  reschedulingInstalmentId,
  onOpenInstalmentDesigner,
  onToggleTenantDeferral,
  togglingTenantDeferral,
}) => {
  const [isModalShowing, setIsModalShowing] = useState(false);
  const { createdAt, instalments } = paymentPlan;

  const sortedInstalments = sortBy(instalments, (i) => i.instalmentNumber);
  const firstInstalment = sortedInstalments && sortedInstalments[0];
  const lastInstalment = sortedInstalments && sortedInstalments[sortedInstalments.length - 1];
  const { firstName, lastName, userId } = paymentPlanTenant;
  const mostRecentPaidInstalment =
    instalments &&
    sortBy(
      instalments.filter((i) => i.status === PaymentPlanInstalmentDTOStatusEnum.PAID),
      (i) => i.dueDate,
    ).pop();
  const paidInstalments = instalments && instalments.filter((i) => i.status === PaymentPlanInstalmentDTOStatusEnum.PAID);
  const totalPaid = paidInstalments && paidInstalments.reduce((acc, instalment) => acc + instalment.amount, 0);

  const items: InlineTableItem[] = [
    { key: 'ID', value: <IdentifierValue>{paymentPlan.id}</IdentifierValue> },
    { key: 'Created Date', value: formatISOStringDate(createdAt) },
    { key: 'Tenant', value: `${firstName} ${lastName}`, linkTo: `/users/${userId}` },
    { key: 'Status', value: paymentPlan.status },
    {
      key: 'Product Type',
      value: paymentPlan.productType ? PAYMENT_PLAN_PRODUCT_TYPE_LABELS[paymentPlan.productType] : 'Not specified',
    },
    { key: 'Total Paid', value: totalPaid ? `£${penceToPounds(totalPaid)}` : '£0.00' },
    { key: 'Number Of Instalments', value: `${instalments && instalments.length}` },
    { key: 'First Instalment', value: firstInstalment ? formatDate(firstInstalment.dueDate) : '' },
    { key: 'Last Instalment', value: lastInstalment ? formatDate(lastInstalment.dueDate) : '' },
    { key: 'Most Recent Paid Date', value: mostRecentPaidInstalment ? formatDate(mostRecentPaidInstalment.dueDate) : 'NA' },
    { key: 'Total Manual Payments', value: `£${penceToPounds(manualPaymentsTotal)}` },
    { key: 'Frequency', value: paymentPlan.frequency },
    { key: 'Historic Plan', value: `${paymentPlan.isHistoric}` },
    { key: 'Interest Added', value: paymentPlan.interestAmount ? `£${penceToPounds(paymentPlan.interestAmount)}` : '£0.00' },
    { key: 'Plan Total', value: `£${penceToPounds(paymentPlan.amount)}` },
  ];

  const areDeferralsDisabled = Boolean(paymentPlan.deferralsDisabledAt);
  items.push({
    key: 'Tenant Deferral',
    value: (
      <TenantDeferralValue>
        <span>{areDeferralsDisabled ? 'Disabled' : 'Enabled'}</span>
        {onToggleTenantDeferral ? (
          <ActionButton
            buttonType="secondary"
            disabled={togglingTenantDeferral}
            mini="true"
            noArrow
            onClick={() => onToggleTenantDeferral(areDeferralsDisabled)}
            type="button"
          >
            {areDeferralsDisabled ? 'Enable tenant deferral' : 'Disable tenant deferral'}
          </ActionButton>
        ) : null}
      </TenantDeferralValue>
    ),
  });
  return (
    <RepositCard
      flush
      title="Payment Plan"
      headerContent={
        <HeaderActions>
          {onOpenInstalmentDesigner ? (
            <ActionButton onClick={onOpenInstalmentDesigner} buttonType={'secondary'}>
              {paymentPlan.status === PaymentPlanDTOStatusEnum.DEFAULT ? 'Reinstate Plan' : 'Modify Instalments'}
            </ActionButton>
          ) : null}
          <ActionButton onClick={() => setIsModalShowing(true)} buttonType={'secondary'}>
            View Schedule
          </ActionButton>
        </HeaderActions>
      }
    >
      {isModalShowing ? (
        <PaymentPlanModal
          paymentPlan={paymentPlan}
          onDismiss={() => setIsModalShowing(false)}
          onDeleteInstalment={onDeleteInstalment}
          deletingInstalmentId={deletingInstalmentId}
          onPreviewInstalmentReschedule={onPreviewInstalmentReschedule}
          onRescheduleInstalment={onRescheduleInstalment}
          previewingRescheduleInstalmentId={previewingRescheduleInstalmentId}
          reschedulingInstalmentId={reschedulingInstalmentId}
        />
      ) : null}
      <InlineTable items={items} />
    </RepositCard>
  );
};
